import { isFunction } from 'lodash-es'
import type { SetStateAction } from 'react'
import { useCallback } from 'react'
import { useObservable } from 'react-use'
import { BehaviorSubject, map } from 'rxjs'
import { attemptParseJSON } from '.'

export const createPersistentBehaviorSubject = <T extends {}>(
  key: string,
  value: T,
) => {
  const initialValue = attemptParseJSON(localStorage.getItem(key)) ?? value

  const $subject = new BehaviorSubject<T>(initialValue)

  $subject.subscribe(value => {
    localStorage.setItem(key, JSON.stringify(value))
  })

  return $subject
}

export const useMapSubjectValue = <K extends string | number | symbol, V>(
  $subject: BehaviorSubject<Record<K, V>>,
  key: K,
  defaultValue: V,
) => {
  const value = useObservable(
    $subject.pipe(map(i => i[key] ?? defaultValue)),
    defaultValue,
  )
  const set = useCallback((action: SetStateAction<V>) => {
    $subject.next({
      ...$subject.value,
      [key]: isFunction(action)
        ? action($subject.value[key] ?? defaultValue)
        : action,
    })
  }, [])
  return [value, set] as const
}

export const createMapSubjectValueHook = <
  K extends string | number | symbol,
  V,
>(
  $subject: BehaviorSubject<Record<K, V>>,
  defaultValue: V,
) => {
  return (key: K, finalDefaultValue = defaultValue) => {
    return useMapSubjectValue($subject, key, finalDefaultValue)
  }
}
